import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  forwardRef
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {
  ContactAutocompleteService,
  ContactAutocompleteResult
} from '../../services/contacts/contact-autocomplete.service';
import { Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  catchError
} from 'rxjs/operators';

@Component({
  selector: 'app-contact-autocomplete',
  templateUrl: './contact-autocomplete.component.html',
  styleUrls: ['./contact-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContactAutocompleteComponent),
      multi: true
    }
  ]
})
export class ContactAutocompleteComponent implements OnInit, ControlValueAccessor {
  @Input() type!: string;
  @Input() placeholder: string = '';
  @Input() panelTheme: "light" | "dark" = "light";
  @Output() selected = new EventEmitter<ContactAutocompleteResult>();

  // This is now an internal form control, not an @Input
  formControl = new FormControl();
  options$: Observable<ContactAutocompleteResult[]> = new Observable();

  // --- ControlValueAccessor properties ---
  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private svc: ContactAutocompleteService) {}

  ngOnInit() {
    this.options$ = this.formControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      // Only search when the value is a non-empty string
      filter(v => typeof v === 'string' && v.length > 0),
      switchMap(q => this.svc.search(this.type, q).pipe(
        catchError(() => of([])) // Handle errors gracefully
      ))
    );
  }

  // --- ControlValueAccessor methods implementation ---
  writeValue(value: any): void {
    // When the parent form patchs a value, we set it to our internal control
    // The value can be the full object or just the display string
    this.formControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.formControl.disable() : this.formControl.enable();
  }

  get panelClass(): string {
    return this.panelTheme === "dark"
      ? "ulaap-contact-autocomplete-panel ulaap-contact-autocomplete-panel--dark"
      : "ulaap-contact-autocomplete-panel";
  }

  contactUuid(opt: ContactAutocompleteResult): string {
    return opt.value || "";
  }

  // --- Component-specific methods ---
  displayFn(opt?: ContactAutocompleteResult | string): string {
    if (!opt) {
      return '';
    }
    // Handle both object and string values
    return typeof opt === 'string' ? opt : opt.label;
  }

  onOptionSelected(opt: ContactAutocompleteResult) {
    // When an option is selected, propagate the full object up to the parent form
    this.onChange(opt);
    this.selected.emit(opt);
  }
  
  // When the input is blurred, call the onTouched callback
  handleBlur() {
    this.onTouched();
  }
}